"use client";

import { useAlternates } from "@commerce-frontend/i18n/alternates";
import { countryOf, languageOf } from "@commerce-frontend/i18n/helpers";
import { isValidLocaleParam } from "@commerce-frontend/i18n/navigation";
import type { Locale } from "@commerce-frontend/i18n/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import { typography } from "@commerce-frontend/ui/base/Typography/typography";
import {
	ModalContent,
	ModalHeader,
	ModalMain,
	ModalOverlay,
	ModalPortal,
	ModalRoot,
	ModalTitle,
} from "@commerce-frontend/ui/components/Modal/Modal";
import { captureException } from "@sentry/nextjs";
import { useQuery } from "@tanstack/react-query";
import Cookie from "js-cookie";
import { useTranslations } from "next-intl";
import { createTranslator } from "next-intl";
import { useState } from "react";
import { useBreakpoint } from "~/hooks/useBreakpoint";
import {
	getSessionStorageItem,
	resetSessionStorageItem,
	setSessionStorageItem,
} from "~/lib/helpers/session-storage";
import { siteConfig, storeConfigs } from "~/lib/store-config";
import { isStoreKey } from "~/lib/store-config/context";
import { useSafeServerLoading } from "~/lib/useSafeServerLoading";
import fallbackLabels from "./CountryModal.labels.json";
import { TranslatableLabel, type TranslationState } from "./TranslatableLabel";

type Props = {
	locale: Locale;
};

type LanguageResponse = {
	country?: string | null;
	locale?: string | null;
};

export function CountryModal({ locale }: Props) {
	const [open, setOpen] = useState(true);
	const [translation, setTranslation] = useState<TranslationState>("none");
	const t = useTranslations("CountryModal");
	const tFallback = createTranslator({ locale: "en", messages: fallbackLabels });
	const siteCountry = countryOf(locale);
	const alternates = useAlternates(locale, siteConfig.locales);
	const isLgScreen = useBreakpoint("lg");
	const isModalDismissed = Cookie.get("countryModalDismissed");
	const animationTimeout = 400;

	const modalOpenChangeHandler = (value: boolean) => {
		setOpen(value);
	};

	const modalCloseHandler = () => {
		Cookie.set("countryModalDismissed", "1", { expires: 365 });
		setOpen(false);
	};

	const toggleTranslationHandler = () => {
		if (translation === "loading") return;

		const nextState = translation === "none" ? "translated" : "none";
		setTranslation("loading");

		setTimeout(() => {
			setTranslation(nextState);
		}, animationTimeout);
	};

	const redirectHandler = (path: string) => {
		window.location.href = path;
	};

	const fetchLanguage = async (): Promise<LanguageResponse | null> => {
		const cachedData = getSessionStorageItem<string>("languageData");
		if (cachedData) {
			try {
				return JSON.parse(cachedData);
			} catch (error) {
				resetSessionStorageItem("languageData");
				captureException(new Error("Failed to parse cached language data."));
			}
		}

		try {
			const res = await fetch("/api/language");
			if (!res.ok) {
				throw new Error("Error retrieving language from API.");
			}
			const data = await res.json();
			setSessionStorageItem("languageData", JSON.stringify(data));
			return data;
		} catch (error) {
			captureException(error);
			return null;
		}
	};

	const { data } = useSafeServerLoading(
		useQuery({
			queryKey: ["countryModal"],
			enabled: !isModalDismissed,
			queryFn: fetchLanguage,
		}),
	);

	// Don't render the modal if the user has dismissed it or we don't get the language data
	if (!data?.country || !data?.locale || isModalDismissed) {
		return;
	}

	const userCountry = data.country; // based on CloudFront-Viewer-Country header
	const userLocale = data.locale; // based on Accept-Language header
	const isUserCountryValid = isStoreKey(userCountry);
	const isUserLocaleValid = isValidLocaleParam(userLocale);

	// Only show the modal if the user is not in the same country as the site, and we have a store with the same country
	if (!isUserCountryValid || userCountry === siteCountry) {
		return;
	}

	// Either use the default locale of the store, or if the store has multiple locales, try to use the user's locale based on the Accept-Language header
	let storeLocale = storeConfigs?.[userCountry]?.defaultLocale;
	const storeLocales = storeConfigs?.[userCountry].locales;
	if (isUserLocaleValid && storeLocales?.length > 1) {
		// Match the exact user locale string
		let matchingLocale = storeLocales.find((locale) => locale === userLocale);

		// If no exact match is found, try using the language only
		if (!matchingLocale) {
			const userLanguage = languageOf(userLocale);
			matchingLocale = storeLocales.find((locale) => locale.startsWith(userLanguage));
		}

		if (matchingLocale) {
			storeLocale = matchingLocale;
		}
	}

	// Translated names of the countries
	const regionLocale = translation === "none" ? "en" : languageOf(locale);
	const regionName = new Intl.DisplayNames([regionLocale], { type: "region" });
	const siteCountryName = regionName.of(siteCountry);
	const userCountryName = regionName.of(userCountry);

	// Only show the modal if there's a redirect available
	const alternatePath = alternates.find((alt) => alt.locale === storeLocale)?.href;

	if (!alternatePath) {
		return;
	}

	return (
		<ModalRoot open={open} onOpenChange={modalOpenChangeHandler}>
			<ModalPortal>
				{/* Show above the Cookie bot banner - needs to have a higher z-index */}
				<ModalOverlay className="z-[2147483632]" />
				<ModalContent
					size="small"
					alignment="center"
					onOpenAutoFocus={(e) => {
						// prevent focus to the close button
						e.preventDefault();
					}}
					// Disallow closing the modal by clicking outside / pressing escape
					onPointerDownOutside={(e) => e.preventDefault()}
					onEscapeKeyDown={(e) => e.preventDefault()}
					onInteractOutside={(e) => e.preventDefault()}
					// Show above the Cookie bot banner - needs to have a higher z-index
					className="flex-col gap-xl lg:p-3xl lg:flex-col z-[2147483633]"
				>
					<ModalMain className="flex flex-col gap-4xl" aria-busy={translation === "loading"}>
						<ModalHeader className="flex flex-col items-start gap-xl">
							<div className="flex size-6xl items-center justify-center rounded-full bg-gray-100 p-md">
								<Icon icon="marker-pin-01" className="size-4xl bg-gray-700" />
							</div>
							<div className="w-full">
								{/* Screen reader title */}
								<ModalTitle>
									<TranslatableLabel
										t={t}
										tFallback={tFallback}
										label="title"
										values={{
											country: siteCountryName,
										}}
										translation={translation}
										skeletonProps={{ className: "d-none" }}
									/>
								</ModalTitle>

								<h3 className={typography("large")}>
									<TranslatableLabel
										t={t}
										tFallback={tFallback}
										label="title"
										values={{
											country: siteCountryName,
										}}
										translation={translation}
										skeletonProps={{ className: "py-1", lineClassName: "h-5" }}
									/>
								</h3>

								<div className={typography("muted")}>
									<TranslatableLabel
										t={t}
										tFallback={tFallback}
										label="subtitle"
										values={{
											siteCountry: siteCountryName,
											userCountry: userCountryName,
										}}
										translation={translation}
										skeletonProps={{
											textLines: isLgScreen ? 2 : 3,
											className: "gap-2 py-1",
											lineClassName: "h-3",
										}}
									/>
								</div>
							</div>
							<div>
								<Button variant="linkColor" size="xl" onClick={() => toggleTranslationHandler()}>
									<TranslatableLabel
										t={t}
										tFallback={tFallback}
										label="show-translation"
										translation={translation}
										loadingLabel="translating"
										reversed={true}
									/>
								</Button>
							</div>
						</ModalHeader>

						<div className="flex flex-col gap-xl">
							<div className="flex w-full shrink-0 flex-col gap-lg lg:flex-row">
								<Button
									className="order-2 w-full lg:order-none lg:w-1/2"
									variant="secondary"
									onClick={() => modalCloseHandler()}
									disabled={translation === "loading"}
								>
									<TranslatableLabel
										t={t}
										tFallback={tFallback}
										label="stay-here"
										translation={translation}
										loadingIcon={true}
									/>
								</Button>
								<Button
									className="order-1 w-full lg:order-none lg:w-1/2"
									variant="primary"
									onClick={() => redirectHandler(alternatePath)}
									disabled={translation === "loading"}
								>
									<TranslatableLabel
										t={t}
										tFallback={tFallback}
										label="visit-website"
										values={{
											country: userCountryName,
										}}
										translation={translation}
										loadingIcon={true}
									/>
								</Button>
							</div>
						</div>
					</ModalMain>
				</ModalContent>
			</ModalPortal>
		</ModalRoot>
	);
}
